import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@arcadeai/design-system/components/ui/atoms/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { useNavigate } from "@tanstack/react-router";
import { Loader2, Trash2 } from "lucide-react";
import { useState } from "react";
import { useSession } from "@/lib/auth-client";
import { useDeleteProjectMember } from "../../api/hooks";

const CANNOT_REMOVE_REASON_COPY: Record<string, string> = {
  not_org_owner: "Only organization owners can remove members.",
  last_member_of_project: "Projects must have at least one member.",
  last_project_in_org: "This is the member's only project in the organization.",
};

export const DeleteMemberButton = ({
  memberId,
  memberEmail,
  canRemove,
  cannotRemoveReason,
}: {
  memberId: string;
  memberEmail: string;
  canRemove: boolean;
  cannotRemoveReason: string | null;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { mutate: deleteMember, isPending } = useDeleteProjectMember();
  const { data: session } = useSession();
  const navigate = useNavigate();
  const isCurrentUser = session?.user.accountId === memberId;

  const handleDelete = () => {
    deleteMember(
      { memberId },
      {
        onSuccess: () => {
          if (isCurrentUser) {
            navigate({ to: "/" });
          }
        },
      }
    );
    setIsOpen(false);
  };

  if (!canRemove) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger render={<span className="inline-flex" />}>
            <Button
              className="pointer-events-none text-muted-foreground/40"
              disabled
              size="icon"
              variant="ghost"
            >
              <Trash2 className="size-4" />
            </Button>
          </TooltipTrigger>
          {cannotRemoveReason && (
            <TooltipContent>
              {CANNOT_REMOVE_REASON_COPY[cannotRemoveReason] ??
                `This member cannot be removed (${cannotRemoveReason})`}
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      <PopoverTrigger
        render={
          <Button
            className="text-destructive/70 hover:text-destructive"
            disabled={isPending}
            size="icon"
            variant="ghost"
          />
        }
      >
        {isPending ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Trash2 className="size-4" />
        )}
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <div className="grid gap-4">
          <div className="space-y-2">
            <h4 className="font-medium leading-none">
              {isCurrentUser ? "Leave Project" : "Remove Member"}
            </h4>
            <p className="text-muted-foreground text-sm">
              {isCurrentUser
                ? "Are you sure you want to leave this project? This action cannot be undone."
                : `Are you sure you want to remove ${memberEmail} from this project? This action cannot be undone.`}
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => setIsOpen(false)}
              size="sm"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              disabled={isPending}
              onClick={handleDelete}
              size="sm"
              variant="destructive"
            >
              {isPending && <Loader2 className="size-4 animate-spin" />}
              {!isPending && (isCurrentUser ? "Leave" : "Delete")}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
